import { RotateCcwIcon } from 'lucide-preact';

import { Button } from '@/components/Buttons';
import { cn } from '@/lib/utils';

export interface GuidedTourButton {
  label: string;
  onClick?: () => void;
}

/**
 * How many buttons still fit on the speech bubble's row. Beyond this they would squeeze the
 * bubble, so they drop back under it. Every authored step currently has one or two.
 */
const INLINE_BUTTON_LIMIT = 2;

interface GuidedTourBannerProps {
  message?: string;
  mascotSrc: string;
  buttons?: GuidedTourButton[];
  onGoBack?: () => void;
  goBackLabel?: string;
  onTakeABreak?: () => void;
  takeABreakLabel?: string;
  className?: string;
}

const GuidedTourBanner = ({
  message,
  mascotSrc,
  buttons = [],
  onGoBack,
  goBackLabel = 'Go Back a Step',
  onTakeABreak,
  takeABreakLabel = 'Explore On My Own',
  className,
}: GuidedTourBannerProps) => {
  // Go Back and Take a Break are both optional (a step opts in via its banner config). With
  // neither one, the right-hand column disappears and the freed width is enough to sit the
  // buttons beside the bubble instead of underneath it.
  const hasSideColumn = Boolean(onGoBack || onTakeABreak);
  const inlineButtons = !hasSideColumn && buttons.length > 0 && buttons.length <= INLINE_BUTTON_LIMIT;
  // Centre the content vertically whenever it isn't stacking under the bubble — with inline buttons
  // OR with none at all, so a message-only banner sits centred like the rest instead of at the top.
  const centeredContent = !hasSideColumn && buttons.length <= INLINE_BUTTON_LIMIT;

  return (
    <div className={cn('preflight relative z-10 h-[139px] shrink-0 grow-0 overflow-hidden bg-forest-core', className)}>
      <img
        src={mascotSrc}
        alt="Zoolife mascot"
        className="preflight preflight-img absolute bottom-0 left-2 w-32 object-contain"
      />

      <div className="preflight flex h-full">
        <div className="preflight w-[147px] shrink-0" />

        <div
          className={cn(
            'preflight flex min-w-0 flex-1',
            // `self-center` drops the fixed row height so this box is only as tall as the bubble;
            // `items-end` then lines the buttons up with the bubble's bottom edge rather than
            // with the banner's, and the pair stays vertically centred in the banner.
            centeredContent ? 'items-end gap-4 self-center pr-[14px]' : 'flex-col pt-[15px]',
          )}
        >
          {message && (
            <div
              className={cn(
                'preflight relative ml-[10px] min-h-[71px] max-w-[889px] rounded-[10px] bg-white px-[15px] pb-4 pt-3',
                inlineButtons && 'min-w-0 flex-1',
              )}
            >
              <div className="preflight absolute left-[-13px] top-[19px] h-0 w-0 border-y-[10px] border-r-[13px] border-y-transparent border-r-white" />
              <p className="preflight preflight-p text-lg font-bold leading-[1.2] text-[#1c1917]">{message}</p>
            </div>
          )}

          {buttons.length > 0 && (
            <div
              className={cn(
                'preflight flex gap-4',
                // Beside the bubble, or stacked under it — never wrapped, the banner is a fixed height.
                inlineButtons ? 'shrink-0' : cn(message ? 'mt-2' : 'ml-[10px] mt-[15px]'),
              )}
            >
              {buttons.map((btn) => (
                <Button
                  key={btn.label}
                  variant="tertiary"
                  onClick={btn.onClick}
                  className="h-8 whitespace-nowrap px-4 font-bold"
                >
                  {btn.label}
                </Button>
              ))}
            </div>
          )}
        </div>

        {hasSideColumn && (
          <div className="preflight flex shrink-0 flex-col items-end gap-8 pr-[14px] pt-[18px]">
            {onGoBack ? (
              <button
                type="button"
                onClick={onGoBack}
                className="preflight preflight-button flex items-center gap-[5px] text-xs font-bold leading-normal text-white"
              >
                <RotateCcwIcon className="h-6 w-6 text-white" />
                {goBackLabel}
              </button>
            ) : (
              // Empty placeholder holding the Go Back row's height (set by its h-6 icon), so
              // Take a Break sits in the same place whether or not the back button is there.
              <span className="preflight block h-6" />
            )}
            {onTakeABreak && (
              <Button
                variant="tertiary"
                onClick={onTakeABreak}
                className="h-[47px] whitespace-nowrap px-5 py-[3px] text-lg font-bold"
              >
                {takeABreakLabel}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GuidedTourBanner;
